import { useEffect } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

interface ScrollRestorationProps {
  /**
   * Whether to restore scroll position on back/forward navigation.
   * @default true - preserves scroll on POP, resets on PUSH
   */
  restoreOnPop?: boolean;
}

/**
 * ScrollRestoration component that handles scroll position management during navigation.
 *
 * By default:
 * - Scrolls to top on forward navigation (PUSH)
 * - Preserves scroll position on browser back/forward (POP)
 *
 * @example
 * ```tsx
 * <ScrollRestoration />
 * ```
 */
export const ScrollRestoration = ({
  restoreOnPop = true,
}: ScrollRestorationProps) => {
  const location = useLocation();
  const navigationType = useNavigationType();

  useEffect(() => {
    // Only scroll to top on forward navigation (PUSH)
    // Preserve scroll position on browser back/forward (POP)
    if (!restoreOnPop || navigationType === "PUSH") {
      window.scrollTo(0, 0);
    }
  }, [location.pathname, navigationType, restoreOnPop]);

  return null;
};
